import { useState } from "react";
import { useGetIdentity, useGetList, useGetOne, useNotify } from "react-admin";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { HiOutlinePaperAirplane, HiOutlineUser } from "react-icons/hi";
import { colors, radius } from "../../data/tokens";

// "Enviar teste" da ficha do template: dispara o template como e-mail
// real para (a) um sócio JÁ CADASTRADO, pelo endpoint de envio manual
// das Operações em Massa (POST /users/mail/send/:userId/:templateId),
// ou (b) um ENDEREÇO AVULSO digitado livre, pelo endpoint de teste
// criado para isso (POST /users/mail/send-test/:templateId — TODO
// item 14). O endereço avulso, quando preenchido e válido, tem
// prioridade sobre o sócio selecionado.
export const SendTestEmailButton = ({ record }: { record: any }) => {
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [recipient, setRecipient] = useState<any>(null);
  const [freeEmail, setFreeEmail] = useState("");
  const [sending, setSending] = useState(false);

  const freeEmailClean = freeEmail.trim();
  const freeEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(freeEmailClean);
  // Destino efetivo do teste: o endereço avulso vale quando digitado
  const targetEmail = freeEmailClean
    ? freeEmailValid
      ? freeEmailClean
      : ""
    : recipient?.email || "";

  // Busca de sócio no padrão do painel: só consulta com 2+ caracteres
  const searchReady = open && search.trim().length >= 2;
  const { data: matches = [], isLoading: searching } = useGetList(
    "users/crud",
    {
      filter: { mailName: search },
      pagination: { page: 1, perPage: 10 },
      sort: { field: "fullName", order: "ASC" },
    },
    { enabled: searchReady }
  );

  // Cadastro do próprio usuário logado (atalho "enviar para mim")
  const { data: myself } = useGetOne(
    "users/crud",
    { id: identity?.id ?? "" },
    { enabled: open && Boolean(identity?.id) }
  );

  // O destinatário escolhido pelo atalho pode não estar entre os
  // resultados da busca — entra na lista para o Autocomplete reconhecê-lo
  const options =
    recipient && !matches.some((match: any) => match.id === recipient.id)
      ? [recipient, ...matches]
      : matches;

  const optionLabel = (option: any) =>
    option?.fullName
      ? `${option.fullName} — ${option.email || "sem e-mail"}`
      : "";

  const close = () => {
    setOpen(false);
    setSearch("");
    setRecipient(null);
    setFreeEmail("");
    setSending(false);
  };

  const sendTest = async () => {
    if (!targetEmail) return;
    setSending(true);
    try {
      const auth = localStorage.getItem("auth");
      const authJson = auth ? JSON.parse(auth) : auth;
      // Endereço avulso usa o endpoint de teste; sócio cadastrado usa o
      // envio manual de sempre
      const url = freeEmailClean
        ? `${process.env.REACT_APP_API_PATH}/users/mail/send-test/${record.id}`
        : `${process.env.REACT_APP_API_PATH}/users/mail/send/${recipient.id}/${record.id}`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + authJson.access_token,
        },
        ...(freeEmailClean
          ? { body: JSON.stringify({ email: targetEmail }) }
          : {}),
      });
      if (!response.ok) {
        notify(`Falha ao enviar o teste para ${targetEmail}.`, {
          type: "error",
        });
        setSending(false);
        return;
      }
      notify(`E-mail de teste enviado para ${targetEmail}.`, {
        type: "success",
      });
      close();
    } catch (error) {
      notify(`Falha ao enviar o teste para ${targetEmail}.`, {
        type: "error",
      });
      setSending(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<HiOutlinePaperAirplane size={16} />}
        onClick={() => setOpen(true)}
      >
        Enviar teste
      </Button>
      <Dialog
        open={open}
        onClose={close}
        PaperProps={{
          sx: {
            borderRadius: `${radius.lg}px`,
            maxWidth: 480,
            width: "100%",
          },
        }}
      >
        <Box sx={{ padding: "20px 24px 0" }}>
          <Typography
            sx={{ fontSize: 18, fontWeight: 700, color: colors.textStrong }}
          >
            Enviar teste do template
          </Typography>
          <Typography
            sx={{ fontSize: 13, color: colors.textMuted, marginTop: 0.25 }}
          >
            “{record.name}” será enviado como um e-mail de verdade para o
            destinatário escolhido.
          </Typography>

          {myself?.email && (
            <Button
              size="small"
              startIcon={<HiOutlineUser size={16} />}
              onClick={() => {
                setRecipient(myself);
                setSearch(optionLabel(myself));
              }}
              sx={{ marginTop: 2, textTransform: "none" }}
            >
              Enviar para mim — {myself.email}
            </Button>
          )}

          <Autocomplete
            sx={{ marginTop: myself?.email ? 1 : 2.5 }}
            options={options}
            value={recipient}
            onChange={(_, value) => setRecipient(value)}
            inputValue={search}
            onInputChange={(_, value) => setSearch(value)}
            getOptionLabel={optionLabel}
            isOptionEqualToValue={(option: any, value: any) =>
              option.id === value.id
            }
            getOptionDisabled={(option: any) => !option.email}
            // A filtragem é do servidor (mailName) — não refiltrar no cliente
            filterOptions={(x) => x}
            loading={searching && searchReady}
            noOptionsText={
              search.trim().length >= 2
                ? "Nenhum sócio encontrado"
                : "Digite nome, e-mail ou CPF (mínimo 2 letras)"
            }
            renderInput={(params) => (
              <TextField {...params} label="Buscar sócio cadastrado" fullWidth />
            )}
          />

          {/* Endereço avulso (fora da base) — tem prioridade quando
              preenchido; o backend valida de novo e exige admin */}
          <TextField
            label="Ou digite um endereço avulso (fora da base)"
            placeholder="alguem@exemplo.com.br"
            value={freeEmail}
            onChange={(e) => setFreeEmail(e.target.value)}
            fullWidth
            sx={{ marginTop: 2 }}
            error={freeEmailClean.length > 0 && !freeEmailValid}
            helperText={
              freeEmailClean.length > 0 && !freeEmailValid
                ? "Endereço inválido"
                : freeEmailClean
                ? "O endereço avulso será usado no lugar do sócio selecionado"
                : undefined
            }
          />

          {targetEmail && (
            <Typography
              sx={{
                fontSize: 13,
                color: colors.textSecondary,
                marginTop: 1.5,
              }}
            >
              O teste irá para: <strong>{targetEmail}</strong>
            </Typography>
          )}

          <Typography
            sx={{ fontSize: 12, color: colors.textMuted, marginTop: 1.5 }}
          >
            Vai a última versão salva do template.
            {record.trigger === "ACCOUNT_RECOVERY" &&
              " Neste template, o link de recuperação não entra no teste — ele só é gerado no fluxo real de esqueci a senha."}
          </Typography>
        </Box>
        <DialogActions sx={{ padding: "16px 20px 20px" }}>
          <Button onClick={close} sx={{ color: colors.textMuted }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={!targetEmail || sending}
            onClick={sendTest}
            startIcon={<HiOutlinePaperAirplane size={16} />}
          >
            {sending ? "Enviando..." : "Enviar teste"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
